import { useContext, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import * as yup from "yup";

import MyButton from "../../components/MyButton";
import MyTextArea from "../../components/inputs/MyTextArea";
import { useBoardItemComment } from "../../hooks/useBoardItemComment";
import { SiteContext } from "../../context/SiteProvider";
import MyTitle from "../../components/MyTitle";
import MyInputFile from "../../components/inputs/MyInputFile";
import MyCheckboxCircle from "../../components/inputs/MyCheckboxCircle";
import { doUploadFiles } from "../../utils/file";
import { useFile } from "../../hooks/useFile";
import { AuthContext } from "../../context/AuthProvider";
import MyInputCaptcha from "../../components/inputs/MyInputCaptcha";

const AnswerForm = ({
  board,
  boardItem,
  selectComment,
  setSelectComment,
  setReplyMode,
}) => {
  const { createBoardItemCommentMutation, updateBoardItemCommentMutation } =
    useBoardItemComment();
  const queryClient = useQueryClient();
  const { setLoadingScreen } = useContext(SiteContext);
  const { getFileUploadUrl } = useFile();
  const { profile, isAuthenticated } = useContext(AuthContext);

  const initValues = {
    isPrivate: boardItem?.isPrivate,
    comment: "",
    captchaToken: "",
    files: [],
    isRequiredCaptcha: false,
  };

  const validationSchema = yup.object({
    comment: yup.string().required("필수 입력 항목입니다."),
    files: yup
      .array()
      .of(yup.mixed().required("파일을 선택해 주세요."))
      .max(5, "최대 5개의 파일만 업로드할 수 있습니다.")
      .nullable(),
    captcha: yup.string().when("isRequiredCaptcha", {
      is: true,
      then: () =>
        yup.string().required("자동입력방지 인증코드를 입력해주세요."),
    }),
  });

  const methods = useForm({
    defaultValues: initValues,
    resolver: yupResolver(validationSchema),
  });

  const showCaptcha = useMemo(
    () => !!board?.options?.useCaptchaInComment,
    [board]
  );

  const onCancel = () => {
    if (!selectComment) setReplyMode(false);
    methods.reset();
    setSelectComment(null);
  };

  const onSubmit = async (vals) => {
    if (board?.boardCode) {
      const payload = {
        comment: vals.comment,
        userId: profile?.userId,
      };

      if (board.options?.usePrivateInComment)
        payload.isPrivate = boardItem?.isPrivate ? true : vals.isPrivate;
      else payload.isPrivate = false;

      if (showCaptcha) {
        if (!vals.captchaToken) {
          methods.setError("captcha", {
            message: "인증을 진행해주세요.",
          });
          return;
        }
        payload.captchaToken = vals.captchaToken;
      }

      setLoadingScreen(true);

      let uploadedFiles = [];
      if (vals.files?.length) {
        uploadedFiles = await doUploadFiles(vals.files, getFileUploadUrl, {
          boardCode: board?.boardCode,
          isComment: true,
        });
      }

      payload.attachedFiles = uploadedFiles.map((file) => ({
        fileName: file.name,
        fileId: file.fileId,
      }));

      if (!selectComment?.commentNo) {
        createBoardItemCommentMutation.mutate(
          {
            boardCode: board?.boardCode,
            itemNo: boardItem?.itemNo,
            payload,
          },
          {
            onSuccess: (res) => {
              if (res) {
                alert("답변이 등록되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: ["/board-item/{boardCode}/{itemNo}"],
                });
                queryClient.invalidateQueries({
                  queryKey: [
                    "/board-item-comment?boardCode={boardCode}&itemNo={itemNo}&page=1",
                  ],
                });
                methods.reset();
                setSelectComment(null);
                setReplyMode(false);
              } else {
                alert("댓글 업로드 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("댓글 업로드 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
              if (showCaptcha) {
                methods.clearErrors("captcha");
                methods.setValue("captcha", "");
                methods.setValue("captchaToken", "");
                queryClient.invalidateQueries({
                  queryKey: ["/site-common/captcha/generate"],
                });
              }
            },
          }
        );
      } else {
        updateBoardItemCommentMutation.mutate(
          {
            boardCode: board?.boardCode,
            commentNo: selectComment.commentNo,
            itemNo: boardItem?.itemNo,
            payload,
          },
          {
            onSuccess: (res) => {
              if (res) {
                alert("답변이 수정되었습니다.");
                queryClient.invalidateQueries({
                  queryKey: [
                    "/board-item-comment?boardCode={boardCode}&itemNo={itemNo}&page=1",
                  ],
                });
                methods.reset();
                setReplyMode(false);
                setSelectComment(null);
              } else {
                alert("댓글 업로드 중 오류가 발생했습니다.");
              }
            },
            onError: () => {
              alert("댓글 업로드 중 오류가 발생했습니다.");
            },
            onSettled: () => {
              setLoadingScreen(false);
              if (showCaptcha) {
                methods.clearErrors("captcha");
                methods.setValue("captcha", "");
                methods.setValue("captchaToken", "");
                queryClient.invalidateQueries({
                  queryKey: ["/site-common/captcha/generate"],
                });
              }
            },
          }
        );
      }
    } else {
      alert("게시글 카테고리가 존재하지 않습니다.");
    }
  };

  useEffect(() => {
    methods.setValue("isRequiredCaptcha", showCaptcha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCaptcha]);

  useEffect(() => {
    if (selectComment?.commentNo) {
      methods.setValue("comment", selectComment.comment);
      methods.setValue("isPrivate", selectComment.isPrivate);

      if (selectComment.attachedFiles?.length) {
        const newFiles = selectComment.attachedFiles.map((file) => ({
          fileId: file.fileId,
          name: file.fileName,
          file: {
            ...file,
            type: file.contentType,
          },
        }));
        methods.setValue("files", newFiles);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectComment]);

  return (
    <div>
      <MyTitle
        title={!selectComment?.commentNo ? "답변 작성" : "답변 수정"}
        className="mb-4"
      />

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex flex-wrap md:mb-5 mb-3">
            <label
              htmlFor="comment"
              className="md:mt-4 md:w-[150px] w-full font-semibold"
            >
              답변내용 <span className="text-error">*</span>
            </label>
            <div className="flex-1">
              <MyTextArea id="comment" name="comment" rows="10" />
            </div>
          </div>

          <div className="flex flex-wrap md:mb-5 mb-3">
            <label
              htmlFor="files"
              className="md:mt-4 md:w-[150px] w-full font-semibold"
            >
              파일 첨부
            </label>
            <div className="flex-1">
              <MyInputFile
                name="files"
                multiple
                placeholder="파일을 첨부해 주세요."
                hint="*파일 첨부는 최대 5개까지 업로드 가능합니다."
              />
            </div>
          </div>

          {board.options?.usePrivateInComment && (
            <div className="flex flex-wrap md:mb-5 mb-3">
              <label
                htmlFor="isPrivate"
                className="md:mt-4 md:w-[150px] w-full font-semibold"
              >
                비밀 글
              </label>
              <div className="flex-1 h-14 flex items-center">
                <MyCheckboxCircle
                  name="isPrivate"
                  label="*체크 시 ,관리자, 게시글 작성자, 답변 작성자만 확인 할 수 있습니다."
                  disabled={boardItem?.isPrivate}
                />
              </div>
            </div>
          )}

          {showCaptcha && (
            <MyInputCaptcha
              name="captchaToken"
              labelClass="md:w-[150px]"
              className={isAuthenticated ? "hidden" : ""}
            />
          )}

          <div className="flex items-center sm:gap-4 gap-2 md:mt-10 mt-5">
            <MyButton
              variant="outlined"
              className="font-semibold ml-auto min-w-[120px]"
              onClick={onCancel}
            >
              <span className="text-gray-dark">취소</span>
            </MyButton>

            <MyButton
              variant="contained"
              color="primary"
              className="font-semibold min-w-[120px]"
              type="submit"
            >
              등록
            </MyButton>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default AnswerForm;
